import React, { useState, useEffect } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import { toast } from 'react-toastify';
import { FaTimes, FaBox, FaCheckCircle, FaShippingFast, FaCog, FaMapMarkerAlt, FaSpinner, FaHistory, FaShieldAlt, FaClock, FaUser, FaCalendarAlt, FaTruckMoving, FaSave } from 'react-icons/fa';
import { MdWarningAmber } from 'react-icons/md';
import { ProjectAsset, AssetLifecycle, AssetWarranty, getProjectAssetLifecycle, getProjectAssetWarranty } from '../../api/assets';
import { getProjectUsers } from '../../api/projects';

interface AssetDetailsModalProps {
  asset: ProjectAsset;
  projectId: string;
  isOpen: boolean;
  onClose: () => void;
  onEdit?: (asset: ProjectAsset) => void;
  onWarrantyEdit?: () => void;
}

export const AssetDetailsModal: React.FC<AssetDetailsModalProps> = ({
  asset,
  projectId,
  isOpen,
  onClose,
  onEdit,
  onWarrantyEdit
}) => {
  const { keycloak } = useKeycloak();
  const [activeTab, setActiveTab] = useState<'overview' | 'lifecycle' | 'warranty'>('overview');
  const [lifecycleData, setLifecycleData] = useState<AssetLifecycle | null>(null);
  const [warrantyData, setWarrantyData] = useState<AssetWarranty | null>(null);
  const [loadingLifecycle, setLoadingLifecycle] = useState(false);
  const [loadingWarranty, setLoadingWarranty] = useState(false);
  const [usersMap, setUsersMap] = useState<Record<string, any>>({});

  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);
  const [editedAsset, setEditedAsset] = useState<ProjectAsset>(asset);
  const [saving, setSaving] = useState(false);

  // Load project users
  const loadProjectUsers = async () => {
    if (!keycloak.token || !projectId) return;
    
    try {
      const usersResponse = await getProjectUsers(keycloak.token, projectId);
      const userLookup: Record<string, any> = {};
      
      // Map both user_id and id fields for flexible lookup
      usersResponse.results.forEach((userWithRole: any) => {
        if (userWithRole.user) {
          // Use user.id as the key (this is what current_responsible_person_id points to)
          userLookup[userWithRole.user.id] = {
            ...userWithRole.user,
            project_role_id: userWithRole.project_role_id,
            escalation_level_id: userWithRole.escalation_level_id
          };
          // Also map by user_id for backward compatibility
          if (userWithRole.user_id) {
            userLookup[userWithRole.user_id] = {
              ...userWithRole.user,
              project_role_id: userWithRole.project_role_id,
              escalation_level_id: userWithRole.escalation_level_id
            };
          }
        }
      });
      
      setUsersMap(userLookup);
      console.log('👥 Project users loaded:', usersResponse.results.length, 'users');
      console.log('🔍 User mapping keys:', Object.keys(userLookup));
    } catch (error) {
      console.error('Error loading project users:', error);
      // Don't show toast error as this is supplementary data
    }
  };

  // Load lifecycle data
  const loadLifecycle = async () => {
    if (!keycloak.token || !projectId || !asset.id) return;
    
    setLoadingLifecycle(true);
    try {
      // Load users and lifecycle data in parallel (statuses now included in lifecycle)
      const [lifecycle] = await Promise.all([
        getProjectAssetLifecycle(keycloak.token, projectId, asset.id),
        Object.keys(usersMap).length === 0 ? loadProjectUsers() : Promise.resolve()
      ]);
      
      setLifecycleData(lifecycle);
      console.log('📈 Asset lifecycle loaded:', lifecycle.length, 'entries');
      console.log('✨ Lifecycle now includes status data directly!');
    } catch (error) {
      console.error('Error loading asset lifecycle:', error);
      toast.error('Failed to load asset lifecycle');
      setLifecycleData([]);
    } finally {
      setLoadingLifecycle(false);
    }
  };

  // Load warranty data
  const loadWarranty = async () => {
    if (!keycloak.token || !projectId || !asset.id) return;
    
    setLoadingWarranty(true);
    try {
      const warranty = await getProjectAssetWarranty(keycloak.token, projectId, asset.id);
      setWarrantyData(warranty);
      console.log('🛡️ Asset warranty loaded:', warranty.id);
    } catch (error) {
      console.error('Error loading asset warranty:', error);
      // Don't show error toast for warranty as it might not exist
      setWarrantyData(null);
    } finally {
      setLoadingWarranty(false);
    }
  };

  // Load data when modal opens
  useEffect(() => {
    if (isOpen) {
      // Always load users when modal opens for editing functionality
      loadProjectUsers();
      
      if (activeTab === 'lifecycle' && !lifecycleData) {
        loadLifecycle();
      }
      if (activeTab === 'warranty' && !warrantyData) {
        loadWarranty();
      }
    }
  }, [isOpen, activeTab]);

  // Load data when tab changes
  useEffect(() => {
    if (isOpen) {
      if (activeTab === 'lifecycle' && !lifecycleData && !loadingLifecycle) {
        loadLifecycle();
      }
      if (activeTab === 'warranty' && !warrantyData && !loadingWarranty && asset.warranty) {
        loadWarranty();
      }
    }
  }, [activeTab, isOpen]);

  // Get asset status display
  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'installed':
      case 'in use':
      case 'active': return '#059669';
      case 'shipped':
      case 'in transit': return '#F59E0B';
      case 'ordered': return '#3B82F6';
      case 'planning to purchase':
      case 'planned': return '#6B7280';
      case 'in warehouse':
      case 'warehouse': return '#6366F1';
      default: return '#6B7280';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'installed':
      case 'in use':
      case 'active': return <FaCheckCircle className="w-4 h-4" />;
      case 'shipped':
      case 'in transit': return <FaShippingFast className="w-4 h-4" />;
      default: return <FaBox className="w-4 h-4" />;
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDateShort = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Edit functions
  const handleStartEdit = () => {
    setEditedAsset(asset);
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setEditedAsset(asset);
    setIsEditing(false);
  };

  const handleSaveEdit = async () => {
    if (!keycloak.token || !projectId) return;
    
    setSaving(true);
    try {
      const { updateProjectAsset } = await import('../../api/assets');
      
      await updateProjectAsset(keycloak.token, projectId, editedAsset.id, {
        project_id: projectId,
        name: editedAsset.name,
        item: editedAsset.item,
        description: editedAsset.description,
        quantity: editedAsset.quantity,
        lead_time: editedAsset.lead_time,
        ordered_at: editedAsset.ordered_at,
        eta: editedAsset.eta,
        esd: editedAsset.esd,
        isd: editedAsset.isd,
        tracking_number: editedAsset.tracking_number,
        current_responsible_person_id: editedAsset.current_responsible_person_id
      });
      
      toast.success('Asset updated successfully');
      setIsEditing(false);
      
      // Call onEdit callback to refresh parent component
      if (onEdit) {
        onEdit(editedAsset);
      }
    } catch (error) {
      console.error('Error updating asset:', error);
      toast.error('Failed to update asset');
    } finally {
      setSaving(false);
    }
  };

  const handleFieldChange = (field: keyof ProjectAsset, value: any) => {
    setEditedAsset(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              {asset.status ? getStatusIcon(asset.status.name) : <FaBox className="w-4 h-4" />}
              <h2 className="text-xl font-semibold text-gray-900 font-['Arcadia_Display',system-ui,sans-serif]">
                {asset.name}
              </h2>
            </div>
            <div 
              className="px-3 py-1 rounded-full text-white text-sm flex items-center space-x-1"
              style={{ backgroundColor: asset.status ? getStatusColor(asset.status.name) : '#6B7280' }}
            >
              <span>{asset.status?.name || 'Unknown'}</span>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {onEdit && (
              <button
                onClick={handleStartEdit}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                title="Edit asset"
              >
                <FaUser className="w-4 h-4" />
                <span>{isEditing ? 'Editing...' : 'Edit'}</span>
              </button>
            )}
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <FaTimes className="w-5 h-5" />
          </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 px-6">
          <button
            onClick={() => setActiveTab('overview')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'overview'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <div className="flex items-center space-x-2">
              <FaBox className="w-4 h-4" />
              <span>Overview</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('lifecycle')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'lifecycle'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <div className="flex items-center space-x-2">
              <FaHistory className="w-4 h-4" />
              <span>Lifecycle</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('warranty')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'warranty'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
            disabled={!asset.warranty}
          >
            <div className="flex items-center space-x-2">
              <FaShieldAlt className="w-4 h-4" />
              <span>Warranty</span>
              {!asset.warranty && <span className="text-xs">(N/A)</span>}
            </div>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {activeTab === 'overview' && (
            <div className="p-6 space-y-6">
              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h3 className="font-semibold text-gray-900 flex items-center space-x-2">
                    <FaBox className="w-4 h-4" />
                    <span>Basic Information</span>
                  </h3>
                  <div className="space-y-3 text-sm">
                    <div>
                      <strong>Item:</strong> 
                      {isEditing ? (
                        <input
                          type="text"
                          value={editedAsset.item}
                          onChange={(e) => handleFieldChange('item', e.target.value)}
                          className="ml-2 px-2 py-1 border border-gray-300 rounded text-sm w-full"
                        />
                      ) : (
                        <span className="ml-2">{asset.item}</span>
                      )}
                    </div>
                    <div>
                      <strong>Description:</strong> 
                      {isEditing ? (
                        <textarea
                          value={editedAsset.description || ''}
                          onChange={(e) => handleFieldChange('description', e.target.value)}
                          className="ml-2 px-2 py-1 border border-gray-300 rounded text-sm w-full h-20 resize-none"
                          placeholder="Enter description..."
                        />
                      ) : (
                        <span className="ml-2">{asset.description || 'N/A'}</span>
                      )}
                    </div>
                    <div>
                      <strong>Quantity:</strong> 
                      {isEditing ? (
                        <input
                          type="number"
                          value={editedAsset.quantity}
                          onChange={(e) => handleFieldChange('quantity', parseInt(e.target.value) || 0)}
                          className="ml-2 px-2 py-1 border border-gray-300 rounded text-sm w-24"
                          min="0"
                        />
                      ) : (
                        <span className="ml-2">{asset.quantity.toLocaleString()}</span>
                      )}
                    </div>
                    <div>
                      <strong>Lead Time:</strong> 
                      {isEditing ? (
                        <input
                          type="number"
                          value={editedAsset.lead_time || ''}
                          onChange={(e) => handleFieldChange('lead_time', e.target.value || null)}
                          className="ml-2 px-2 py-1 border border-gray-300 rounded text-sm w-24"
                          placeholder="Days"
                          min="0"
                        />
                      ) : (
                        <span className="ml-2">{asset.lead_time ? `${asset.lead_time} days` : 'N/A'}</span>
                      )}
                    </div>
                    <div><strong>Created:</strong> {formatDate(asset.created_at)}</div>
                    <div><strong>Last Updated:</strong> {formatDate(asset.updated_at)}</div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="font-semibold text-gray-900 flex items-center space-x-2">
                    <FaTruckMoving className="w-4 h-4" />
                    <span>Procurement & Delivery</span>
                  </h3>
                  <div className="space-y-3 text-sm">
                    <div>
                      <strong>Ordered:</strong> 
                      {isEditing ? (
                        <input
                          type="date"
                          value={editedAsset.ordered_at ? editedAsset.ordered_at.split('T')[0] : ''}
                          onChange={(e) => handleFieldChange('ordered_at', e.target.value)}
                          className="ml-2 px-2 py-1 border border-gray-300 rounded text-sm"
                        />
                      ) : (
                        <span className="ml-2">{formatDateShort(asset.ordered_at)}</span>
                      )}
                  </div>
                    <div>
                      <strong>ETA:</strong> 
                      {isEditing ? (
                        <input
                          type="date"
                          value={editedAsset.eta ? editedAsset.eta.split('T')[0] : ''}
                          onChange={(e) => handleFieldChange('eta', e.target.value)}
                          className="ml-2 px-2 py-1 border border-gray-300 rounded text-sm"
                        />
                      ) : (
                        <span className="ml-2">{formatDateShort(asset.eta)}</span>
                      )}
                    </div>
                    <div>
                      <strong>ESD:</strong> 
                      {isEditing ? (
                        <input
                          type="date"
                          value={editedAsset.esd ? editedAsset.esd.split('T')[0] : ''}
                          onChange={(e) => handleFieldChange('esd', e.target.value || null)}
                          className="ml-2 px-2 py-1 border border-gray-300 rounded text-sm"
                        />
                      ) : (
                        <span className="ml-2">{formatDateShort(asset.esd)}</span>
                      )}
                    </div>
                    <div>
                      <strong>ISD:</strong> 
                      {isEditing ? (
                        <input
                          type="date"
                          value={editedAsset.isd ? editedAsset.isd.split('T')[0] : ''}
                          onChange={(e) => handleFieldChange('isd', e.target.value || null)}
                          className="ml-2 px-2 py-1 border border-gray-300 rounded text-sm"
                        />
                      ) : (
                        <span className="ml-2">{formatDateShort(asset.isd)}</span>
                      )}
                    </div>
                    <div>
                      <strong>Tracking:</strong> 
                      {isEditing ? (
                        <input
                          type="text"
                          value={editedAsset.tracking_number || ''}
                          onChange={(e) => handleFieldChange('tracking_number', e.target.value || null)}
                          className="ml-2 px-2 py-1 border border-gray-300 rounded text-sm w-full"
                          placeholder="Tracking number"
                        />
                      ) : (
                        <span className="ml-2">{asset.tracking_number || 'N/A'}</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Status & Responsible Person */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h3 className="font-semibold text-gray-900 flex items-center space-x-2">
                    <FaCog className="w-4 h-4" />
                    <span>Status Information</span>
                  </h3>
                  {asset.status && (
                    <div className="bg-gray-50 p-4 rounded-lg space-y-2 text-sm">
                      <div className="flex items-center space-x-2">
                        {getStatusIcon(asset.status.name)}
                        <span 
                          className="px-2 py-1 rounded-full text-white text-xs"
                          style={{ backgroundColor: getStatusColor(asset.status.name) }}
                        >
                          {asset.status.name}
                        </span>
                      </div>
                      <div><strong>Description:</strong> {asset.status.description || 'N/A'}</div>
                      <div><strong>Order:</strong> {asset.status.status_order}</div>
                      <div><strong>Est. Duration:</strong> {asset.status.estimated_duration_days || 0} days</div>
                      <div><strong>Max Duration:</strong> {asset.status.max_duration_days || 0} days</div>
                    </div>
                  )}
                </div>

                  <div className="space-y-4">
                    <h3 className="font-semibold text-gray-900 flex items-center space-x-2">
                      <FaUser className="w-4 h-4" />
                      <span>Responsible Person</span>
                    </h3>
                  {isEditing ? (
                    <div className="space-y-3">
                      <select
                        value={editedAsset.current_responsible_person_id || ''}
                        onChange={(e) => handleFieldChange('current_responsible_person_id', e.target.value || null)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">Select responsible person...</option>
                        {Object.values(usersMap).map((user: any) => (
                          <option key={user.id} value={user.id}>
                            {user.first_name} {user.last_name} ({user.email})
                          </option>
                        ))}
                      </select>
                      {editedAsset.current_responsible_person_id && usersMap[editedAsset.current_responsible_person_id] && (
                        <div className="bg-blue-50 p-3 rounded-lg space-y-1 text-sm">
                          <div><strong>Selected:</strong> {usersMap[editedAsset.current_responsible_person_id].first_name} {usersMap[editedAsset.current_responsible_person_id].last_name}</div>
                          <div><strong>Email:</strong> {usersMap[editedAsset.current_responsible_person_id].email}</div>
                          <div><strong>Username:</strong> {usersMap[editedAsset.current_responsible_person_id].username}</div>
                        </div>
                      )}
                    </div>
                  ) : (
                    asset.current_responsible_person ? (
                    <div className="bg-blue-50 p-4 rounded-lg space-y-2 text-sm">
                      <div><strong>Name:</strong> {asset.current_responsible_person.first_name} {asset.current_responsible_person.last_name}</div>
                      <div><strong>Email:</strong> {asset.current_responsible_person.email}</div>
                      <div><strong>Username:</strong> {asset.current_responsible_person.username}</div>
                      <div><strong>Active:</strong> {asset.current_responsible_person.is_active ? 'Yes' : 'No'}</div>
                    </div>
                    ) : (
                      <div className="bg-gray-50 p-4 rounded-lg text-sm text-gray-500">
                        No responsible person assigned
                  </div>
                    )
                )}
                </div>
              </div>

              {/* Installation Location */}
              {asset.is_installed && asset.unit && (
                <div className="space-y-4">
                  <h3 className="font-semibold text-gray-900 flex items-center space-x-2">
                    <FaMapMarkerAlt className="w-4 h-4" />
                    <span>Installation Location</span>
                  </h3>
                  <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div><strong>Rack ID:</strong> {asset.unit.rack_id}</div>
                      <div><strong>Position:</strong> {asset.unit.position}U</div>
                      <div><strong>Reserved Units:</strong> {asset.unit.reserved_units}U</div>
                      <div><strong>Device Model:</strong> {asset.unit.device_model || 'N/A'}</div>
                      <div><strong>Power Usage:</strong> {asset.unit.power_usage || 0}W</div>
                      <div><strong>Horizontal Position:</strong> {asset.unit.horizontal_position || 'N/A'}</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Notes */}
              {asset.notes && (
                <div className="space-y-4">
                  <h3 className="font-semibold text-gray-900">Notes</h3>
                  <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                    <p className="text-sm text-gray-700">{asset.notes}</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'lifecycle' && (
            <div className="p-6 space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-gray-900 flex items-center space-x-2">
                  <FaHistory className="w-4 h-4" />
                  <span>Asset Lifecycle History</span>
                </h3>
                <button
                  onClick={loadLifecycle}
                  disabled={loadingLifecycle}
                  className="flex items-center space-x-2 px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {loadingLifecycle ? <FaSpinner className="w-3 h-3 animate-spin" /> : <FaHistory className="w-3 h-3" />}
                  <span>{loadingLifecycle ? 'Loading...' : 'Refresh'}</span>
                </button>
              </div>

              {loadingLifecycle && (
                <div className="flex items-center justify-center py-8">
                  <FaSpinner className="w-6 h-6 animate-spin text-blue-600" />
                  <span className="ml-2 text-gray-600">Loading lifecycle data...</span>
                </div>
              )}

              {lifecycleData && lifecycleData.length > 0 && (
                <div className="space-y-4">
                  {/* Current Responsible Person Summary */}
                  {asset.current_responsible_person && (
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4 mb-6">
                      <h4 className="font-semibold text-blue-900 mb-2 flex items-center space-x-2">
                        <FaUser className="w-4 h-4" />
                        <span>Current Responsible Person</span>
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                        <div><strong>Name:</strong> {asset.current_responsible_person.first_name} {asset.current_responsible_person.last_name}</div>
                        <div><strong>Email:</strong> {asset.current_responsible_person.email}</div>
                        <div><strong>Username:</strong> {asset.current_responsible_person.username}</div>
                      </div>
                    </div>
                  )}
                  
                  {/* Lifecycle History */}
                  {lifecycleData.map((entry, index) => {
                    // Status now comes directly in entry.status (no need for statusMap lookup!)
                    const entryStatus = entry.status;
                    const previousEntry = index > 0 ? lifecycleData[index - 1] : null;
                    
                    // Map responsible person from usersMap
                    const responsiblePerson = entry.current_responsible_person_id ? usersMap[entry.current_responsible_person_id] : null;
                    const previousResponsiblePerson = previousEntry?.current_responsible_person_id ? usersMap[previousEntry.current_responsible_person_id] : null;
                    
                    // Check if status requires responsible person but none is assigned
                    const requiresResponsiblePerson = entryStatus?.requires_responsible_person === true;
                    const hasResponsiblePerson = !!responsiblePerson;
                    const missingRequiredPerson = requiresResponsiblePerson && !hasResponsiblePerson;
                    
                    console.log('🔍 Entry responsible person ID:', entry.current_responsible_person_id);
                    console.log('🔍 Found responsible person:', responsiblePerson);
                    console.log('⚠️ Missing required responsible person:', missingRequiredPerson);
                    
                    // Determine what changed from previous entry
                    const changes: string[] = [];
                    if (previousEntry) {
                      if (entry.current_status_id !== previousEntry.current_status_id) {
                        const prevStatus = previousEntry.status;
                        changes.push(`Status: ${prevStatus?.name || 'Unknown'} → ${entryStatus?.name || 'Unknown'}`);
                      }
                      if (entry.current_responsible_person_id !== previousEntry.current_responsible_person_id) {
                        const prevPersonName = previousResponsiblePerson ? `${previousResponsiblePerson.first_name} ${previousResponsiblePerson.last_name}` : 'Unassigned';
                        const currentPersonName = responsiblePerson ? `${responsiblePerson.first_name} ${responsiblePerson.last_name}` : 'Unassigned';
                        changes.push(`Responsible: ${prevPersonName} → ${currentPersonName}`);
                      }
                      if (entry.quantity !== previousEntry.quantity) {
                        changes.push(`Quantity: ${previousEntry.quantity} → ${entry.quantity}`);
                      }
                      if (entry.tracking_number !== previousEntry.tracking_number) {
                        changes.push(`Tracking: ${previousEntry.tracking_number || 'None'} → ${entry.tracking_number || 'None'}`);
                      }
                      if (entry.ordered_at !== previousEntry.ordered_at) {
                        changes.push(`Ordered: ${formatDateShort(previousEntry.ordered_at) || 'Not ordered'} → ${formatDateShort(entry.ordered_at) || 'Not ordered'}`);
                      }
                      if (entry.eta !== previousEntry.eta) {
                        changes.push(`ETA: ${formatDateShort(previousEntry.eta) || 'Not set'} → ${formatDateShort(entry.eta) || 'Not set'}`);
                      }
                      if (entry.is_installed !== previousEntry.is_installed) {
                        changes.push(`Installation: ${previousEntry.is_installed ? 'Installed' : 'Not Installed'} → ${entry.is_installed ? 'Installed' : 'Not Installed'}`);
                      }
                      if (entry.unit_id !== previousEntry.unit_id) {
                        changes.push(`Location: ${previousEntry.unit_id ? 'Unit ' + previousEntry.unit_id?.slice(-8) : 'No location'} → ${entry.unit_id ? 'Unit ' + entry.unit_id?.slice(-8) : 'No location'}`);
                      }
                    }
                    
                    return (
                      <div key={`${entry.id}-${entry.version}-${index}`} className="bg-white border border-gray-200 rounded-lg p-4">
                        <div className="flex items-start space-x-4">
                          <div className="flex-shrink-0">
                            {entryStatus ? getStatusIcon(entryStatus.name) : <FaBox className="w-4 h-4 text-gray-400" />}
                          </div>
                          <div className="flex-1 space-y-3">
                            {/* Header with Status & Date */}
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-2">
                                {entryStatus ? (
                                  <span 
                                    className="px-2 py-1 rounded-full text-white text-xs"
                                    style={{ backgroundColor: getStatusColor(entryStatus.name) }}
                                  >
                                    {entryStatus.name}
                                  </span>
                                ) : (
                                  <span className="px-2 py-1 rounded-full bg-gray-400 text-white text-xs">
                                    Status Unknown
                                  </span>
                                )}
                                {responsiblePerson ? (
                                  <span className="px-2 py-1 rounded-full bg-green-100 text-green-800 text-xs font-medium">
                                    👤 {responsiblePerson.first_name} {responsiblePerson.last_name}
                                  </span>
                                ) : missingRequiredPerson ? (
                                  <span className="px-2 py-1 rounded-full bg-red-100 text-red-800 text-xs font-medium">
                                    🚨 Required but Unassigned
                                  </span>
                                ) : (
                                  <span className="px-2 py-1 rounded-full bg-orange-100 text-orange-800 text-xs font-medium">
                                    ⚠️ Unassigned
                                  </span>
                                )}
                                <span className="text-sm text-gray-500">
                                  {index === 0 ? 'Created' : `Update ${lifecycleData.length - index}`}
                                </span>
                              </div>
                              <div className="flex items-center space-x-1 text-xs text-gray-500">
                                <FaCalendarAlt className="w-3 h-3" />
                                <span>{formatDate(entry.created_at)}</span>
                              </div>
                            </div>
                            
                            {/* Changes Summary */}
                            {changes.length > 0 && (
                              <div className="bg-blue-50 border-l-4 border-blue-400 p-3 rounded-r">
                                <div className="text-sm font-medium text-blue-800 mb-2">Changes:</div>
                                <div className="space-y-1">
                                  {changes.map((change, changeIndex) => (
                                    <div key={changeIndex} className="text-xs text-blue-700">• {change}</div>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                                                         {/* Current State Details */}
                             <div className="bg-gray-50 p-3 rounded">
                               <div className="text-sm font-medium text-gray-800 mb-2">Current State:</div>
                               <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                                 <div><strong>Item:</strong> {entry.item}</div>
                                 <div><strong>Quantity:</strong> {entry.quantity.toLocaleString()}</div>
                                 {entryStatus && (
                                   <>
                                     <div><strong>Status Order:</strong> {entryStatus.status_order}</div>
                                     <div><strong>Est. Duration:</strong> {entryStatus.estimated_duration_days ? `${entryStatus.estimated_duration_days} days` : 'N/A'}</div>
                                     <div><strong>Max Duration:</strong> {entryStatus.max_duration_days ? `${entryStatus.max_duration_days} days` : 'N/A'}</div>
                                   </>
                                 )}
                                {entry.ordered_at && (
                                  <div><strong>Ordered:</strong> {formatDateShort(entry.ordered_at)}</div>
                                )}
                                {entry.eta && (
                                  <div><strong>ETA:</strong> {formatDateShort(entry.eta)}</div>
                                )}
                                {entry.tracking_number && (
                                  <div><strong>Tracking:</strong> {entry.tracking_number}</div>
                                )}
                                {entry.lead_time && (
                                  <div><strong>Lead Time:</strong> {entry.lead_time}</div>
                                )}
                                {entry.esd && (
                                  <div><strong>ESD:</strong> {formatDateShort(entry.esd)}</div>
                                )}
                                {entry.isd && (
                                  <div><strong>ISD:</strong> {formatDateShort(entry.isd)}</div>
                                )}
                                                                 <div><strong>Installed:</strong> {entry.is_installed ? 'Yes' : 'No'}</div>
                                 {entry.unit_id && (
                                   <div><strong>Unit ID:</strong> {entry.unit_id.slice(-8)}</div>
                                 )}
                                 <div><strong>Responsible:</strong> {responsiblePerson ? `${responsiblePerson.first_name} ${responsiblePerson.last_name}` : 'Unassigned'}</div>
                                 {responsiblePerson?.email && (
                                   <div><strong>Email:</strong> {responsiblePerson.email}</div>
                                 )}
                              </div>
                                                         </div>
                             
                             {/* Status Requirements */}
                             {entryStatus && (entryStatus.requires_responsible_person || entryStatus.requires_installation || entryStatus.requires_documentation) && (
                               <div className={`border-l-4 p-3 rounded-r ${
                                 missingRequiredPerson ? 'bg-red-50 border-red-400' : 'bg-blue-50 border-blue-400'
                               }`}>
                                 <div className={`text-sm font-medium mb-2 ${
                                   missingRequiredPerson ? 'text-red-800' : 'text-blue-800'
                                 }`}>
                                   {missingRequiredPerson ? '🚨 Status Requirements (Missing!)' : 'ℹ️ Status Requirements'}
                                 </div>
                                 <div className="space-y-1 text-xs">
                                   {entryStatus.requires_responsible_person && (
                                     <div className={`flex items-center space-x-1 ${
                                       !hasResponsiblePerson ? 'text-red-700' : 'text-blue-700'
                                     }`}>
                                       <span>{!hasResponsiblePerson ? '❌' : '✅'}</span>
                                       <span>Responsible Person Required</span>
                                     </div>
                                   )}
                                   {entryStatus.requires_installation && (
                                     <div className={`flex items-center space-x-1 ${
                                       !entry.is_installed ? 'text-red-700' : 'text-blue-700'
                                     }`}>
                                       <span>{!entry.is_installed ? '❌' : '✅'}</span>
                                       <span>Installation Required</span>
                                     </div>
                                   )}
                                   {entryStatus.requires_documentation && (
                                     <div className="flex items-center space-x-1 text-blue-700">
                                       <span>📋</span>
                                       <span>Documentation Required</span>
                                     </div>
                                   )}
                                 </div>
                               </div>
                             )}
                             
                             {/* Notes */}
                            {entry.notes && (
                              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 rounded-r">
                                <div className="text-sm font-medium text-yellow-800">Notes:</div>
                                <div className="text-xs text-yellow-700 mt-1">{entry.notes}</div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {lifecycleData && lifecycleData.length === 0 && !loadingLifecycle && (
                <div className="text-center py-8">
                  <FaHistory className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Lifecycle Data</h3>
                  <p className="text-gray-600">
                    This asset has no recorded lifecycle history yet. Changes to status, 
                    installation, tracking, and other properties will appear here.
                  </p>
                </div>
              )}

              {!lifecycleData && !loadingLifecycle && (
                <div className="text-center py-8">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                    <FaHistory className="mx-auto h-8 w-8 text-blue-600 mb-3" />
                    <h3 className="text-lg font-medium text-blue-900 mb-2">Click Refresh to Load Lifecycle</h3>
                    <p className="text-blue-700 text-sm">
                      Asset lifecycle data will be loaded when you click the refresh button above.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'warranty' && (
            <div className="p-6 space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-gray-900 flex items-center space-x-2">
                  <FaShieldAlt className="w-4 h-4" />
                  <span>Warranty Information</span>
                </h3>
                <div className="flex items-center space-x-3">
                  <button
                    onClick={loadWarranty}
                    disabled={loadingWarranty}
                    className="flex items-center space-x-2 px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {loadingWarranty ? <FaSpinner className="w-3 h-3 animate-spin" /> : <FaShieldAlt className="w-3 h-3" />}
                    <span>{loadingWarranty ? 'Loading...' : 'Refresh'}</span>
                  </button>
                  {onWarrantyEdit && asset.warranty && (
                    <button
                      onClick={onWarrantyEdit}
                      className="flex items-center space-x-2 px-3 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700"
                    >
                      <FaCog className="w-3 h-3" />
                      <span>Edit Warranty</span>
                    </button>
                  )}
                </div>
              </div>

              {loadingWarranty && (
                <div className="flex items-center justify-center py-8">
                  <FaSpinner className="w-6 h-6 animate-spin text-blue-600" />
                  <span className="ml-2 text-gray-600">Loading warranty data...</span>
                </div>
              )}

              {(warrantyData || asset.warranty) && !loadingWarranty && (
                <div className="space-y-6">
                  {/* Warranty Status Card */}
                  <div className={`p-6 rounded-lg border-2 ${
                    (warrantyData || asset.warranty)?.is_active_warranty
                      ? 'bg-green-50 border-green-200'
                      : 'bg-red-50 border-red-200'
                  }`}>
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-2">
                        <FaShieldAlt className={`w-5 h-5 ${
                          (warrantyData || asset.warranty)?.is_active_warranty ? 'text-green-600' : 'text-red-600'
                        }`} />
                        <span className={`font-semibold text-lg ${
                          (warrantyData || asset.warranty)?.is_active_warranty ? 'text-green-800' : 'text-red-800'
                        }`}>
                          {(warrantyData || asset.warranty)?.is_active_warranty ? 'Active Warranty' : 'Expired Warranty'}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2 text-sm">
                        <FaClock className={`w-4 h-4 ${
                          (warrantyData || asset.warranty)?.is_active_warranty ? 'text-green-600' : 'text-red-600'
                        }`} />
                        <span className={
                          (warrantyData || asset.warranty)?.is_active_warranty ? 'text-green-800' : 'text-red-800'
                        }>
                          {(warrantyData || asset.warranty)?.is_active_warranty 
                            ? `${(warrantyData || asset.warranty)?.days_remaining} days remaining`
                            : 'Expired'
                          }
                        </span>
                      </div>
                    </div>

                                         {(() => {
                       const warranty = warrantyData || asset.warranty;
                       return warranty?.days_remaining && 
                              warranty.days_remaining <= 30 && 
                              warranty.is_active_warranty;
                     })() && (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                        <div className="flex items-center space-x-2">
                          <MdWarningAmber className="w-4 h-4 text-yellow-600" />
                          <span className="text-sm text-yellow-800">
                            Warning: Warranty expires in {(warrantyData || asset.warranty)?.days_remaining} days
                          </span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Warranty Details */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <h4 className="font-medium text-gray-900">Warranty Details</h4>
                      <div className="space-y-3 text-sm">
                        <div><strong>Provider:</strong> {(warrantyData || asset.warranty)?.warranty_provider}</div>
                        <div><strong>Status:</strong> {(warrantyData || asset.warranty)?.warranty_status}</div>
                        <div><strong>Start Date:</strong> {formatDateShort((warrantyData || asset.warranty)?.warranty_start_date || null)}</div>
                        <div><strong>End Date:</strong> {formatDateShort((warrantyData || asset.warranty)?.warranty_end_date || null)}</div>
                        <div><strong>Terms:</strong> {(warrantyData || asset.warranty)?.warranty_terms || 'N/A'}</div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <h4 className="font-medium text-gray-900">Product Details</h4>
                      <div className="space-y-3 text-sm">
                        <div><strong>Model:</strong> {(warrantyData || asset.warranty)?.model}</div>
                        <div><strong>Serial Number:</strong> {(warrantyData || asset.warranty)?.serial_number}</div>
                        <div><strong>Coverage:</strong> {(warrantyData || asset.warranty)?.coverage_details || 'Standard'}</div>
                      </div>
                    </div>
                  </div>

                  {/* Manufacturer Information */}
                  {(warrantyData || asset.warranty)?.manufacturer && (
                    <div className="space-y-4">
                      <h4 className="font-medium text-gray-900">Manufacturer</h4>
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                          <div><strong>Name:</strong> {(warrantyData || asset.warranty)?.manufacturer.name}</div>
                          <div><strong>Country:</strong> {(warrantyData || asset.warranty)?.manufacturer.country || 'N/A'}</div>
                          <div><strong>Email:</strong> {(warrantyData || asset.warranty)?.manufacturer.contact_email || 'N/A'}</div>
                          <div><strong>Phone:</strong> {(warrantyData || asset.warranty)?.manufacturer.contact_phone || 'N/A'}</div>
                          {(warrantyData || asset.warranty)?.manufacturer.website && (
                            <div className="md:col-span-2">
                              <strong>Website:</strong> 
                              <a 
                                href={(warrantyData || asset.warranty)?.manufacturer.website || undefined} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="ml-2 text-blue-600 hover:text-blue-800 underline"
                              >
                                {(warrantyData || asset.warranty)?.manufacturer.website}
                              </a>
                            </div>
                          )}
                        </div>
                        {(warrantyData || asset.warranty)?.manufacturer.description && (
                          <div className="mt-3">
                            <strong>Description:</strong>
                            <p className="text-gray-600 mt-1">{(warrantyData || asset.warranty)?.manufacturer.description}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {!asset.warranty && !warrantyData && !loadingWarranty && (
                <div className="text-center py-8">
                  <FaShieldAlt className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Warranty Information</h3>
                  <p className="text-gray-600">This asset does not have warranty information available.</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end px-6 py-4 border-t border-gray-200 space-x-3">
          {isEditing ? (
            <>
              <button
                onClick={handleCancelEdit}
                className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                disabled={saving}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                className="flex items-center space-x-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
                disabled={saving}
              >
                {saving ? (
                  <FaSpinner className="w-4 h-4 animate-spin" />
                ) : (
                  <FaSave className="w-4 h-4" />
                )}
                <span>{saving ? 'Saving...' : 'Save'}</span>
              </button>
            </>
          ) : (
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Close
          </button>
          )}
        </div>
      </div>
    </div>
  );
}; 